'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Viewport width at or below which the inline LTD calendar is collapsed out of
 * the layout and only reachable through the sticky bottom bar ("Find Tickets"
 * on the Show Detail Page, "Change Date" on the Seat Selection Page).
 *
 * 480 is not a design choice — it is the VENDOR's own breakpoint, measured
 * against the live widget (Chromium, `LTD.Calendar` from
 * calendar.finale-cdn.uk): at ≤480px it renders no inline calendar table at all
 * and `LTD.Calendar.toggle()` mounts a full-screen overlay; at ≥481px it renders
 * inline and `toggle()` is a no-op. Collapsing our panel at any wider breakpoint
 * would hide the calendar in a band where nothing can reopen it. The reference
 * CMS hardcodes the same 480 (`calendarScreenSize` in
 * `theaters-cms-reference-code/client-js/theatre-detail-page.js`,
 * `CONFIG.MOBILE_BREAKPOINT` in `seatplan.js`) — same reason.
 *
 * Above it, the bottom bars (which stay visible up to 768px) fall through to
 * `openSheet`'s desktop branch and scroll the inline calendar into view.
 */
export const MOBILE_CALENDAR_BREAKPOINT = 480;

/** `document.body` class paired with `body.shows-mobile-sheet-open` in shows.css. */
const BODY_LOCK_CLASS = 'shows-mobile-sheet-open';

/**
 * The vendor's own full-screen mobile overlay, confirmed live at 390px:
 * `<div class="_calendar-wrapper--overlay_1ye9r_18 widget-calendar-main-wrapper--overlay">`,
 * `position: fixed; top: 0; z-index: 9999`. The unprefixed class is the stable
 * one (the `_…_1ye9r_18` hash is build-specific and has already changed once
 * since the CMS reference was written — its stylesheets pin
 * `._calendar-wrapper--mobile-opened_1ye9r_12`), so match on both.
 *
 * Exported because `OverlayTicketHeaderPortal` has to find the SAME overlay to
 * inject the Tickets header into it — two copies of this selector would drift
 * apart the next time the vendor rebuilds and the hash changes again.
 */
// const VENDOR_OVERLAY_SELECTOR =
export const VENDOR_OVERLAY_SELECTOR =
  '.widget-calendar-main-wrapper--overlay, [class*="calendar-wrapper--overlay"], [class*="calendar-wrapper--mobile-opened"]';

/** How long to wait for the vendor overlay after `toggle()` before giving up. */
const OVERLAY_WAIT_MS = 5000;

type LtdCalendarGlobal = {
  LTD?: { Calendar?: { toggle?: () => void } };
};

/** Calls the vendor's documented single public toggle, if the script is up. */
function toggleVendorCalendar(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as LtdCalendarGlobal;
  if (typeof w.LTD?.Calendar?.toggle === 'function') {
    w.LTD.Calendar.toggle();
    return true;
  }
  return false;
}

export interface MobileCalendarSheet {
  /** True while the vendor's mobile calendar overlay is actually up. Drives the
   *  `data-mobile-calendar-open` attribute the CSS keys off. */
  open: boolean;
  /** Bottom-bar handler. On mobile: expands the vendor widget into its own
   *  full-screen overlay. On desktop it falls back to scrolling the inline
   *  panel into view, since there the calendar is always visible and there is
   *  nothing to open. */
  openSheet: () => void;
  /** Escape / resize-to-desktop. The overlay's own close control is handled by
   *  the observer below rather than through here. */
  closeSheet: () => void;
  /** Spread onto the page root: `<div className="shows-detail-page" {...sheetProps}>`. */
  sheetProps: { 'data-mobile-calendar-open': 'true' | 'false' };
}

/**
 * Shared mobile calendar state for the Show Detail Page and the Seat Selection
 * Page. Both render the SAME `LtdCalendarWidget`, so both get the same
 * open/close semantics from here rather than each growing its own copy — the
 * calendar component itself is never duplicated or remounted, only revealed
 * (see the `height: 0` vs `display: none` note in shows.css).
 *
 * `open` deliberately tracks the VENDOR's overlay rather than our own intent:
 * the overlay is what the user actually sees, it carries its own close control,
 * and if `toggle()` never produces one (script blocked, no `eventUrlId`) we must
 * not hide the bottom bar and strand the user with nothing on screen.
 *
 * @param fallbackSelector Desktop-only: element scrolled into view when the
 *        bottom-bar handler fires above the mobile breakpoint.
 */
export function useMobileCalendarSheet(fallbackSelector?: string): MobileCalendarSheet {
  const [open, setOpen] = useState(false);
  // True between `toggle()` and the overlay appearing — arms the observer below
  // without yet claiming the sheet is open.
  const [awaitingOverlay, setAwaitingOverlay] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;

  const isMobile = useCallback(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_CALENDAR_BREAKPOINT,
    [],
  );

  const openSheet = useCallback(() => {
    if (typeof window === 'undefined') return;

    if (!isMobile()) {
      // Desktop: the calendar is already inline — the CMS's own `openMobileWidget()`
      // returns early above its breakpoint for the same reason. Keep the existing
      // "scroll the panel into view" affordance the header's "Buy Tickets" relied on.
      const panel =
        (fallbackSelector && document.querySelector(fallbackSelector)) ||
        document.getElementById('ticket-booking-panel') ||
        document.getElementById('ltd-calendar-widget');
      panel?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (openRef.current) return;
    // Mirrors `openMobileWidget()` in the reference CMS, which likewise calls
    // `window.LTD.Calendar.toggle()` first and then watches for the overlay.
    toggleVendorCalendar();
    setAwaitingOverlay(true);
  }, [fallbackSelector, isMobile]);

  const closeSheet = useCallback(() => {
    setAwaitingOverlay(false);
    setOpen((wasOpen) => {
      // `toggle()` is symmetric — only fire it if the overlay is actually up,
      // or we would re-OPEN it.
      if (wasOpen) toggleVendorCalendar();
      return false;
    });
  }, []);

  /**
   * Single source of truth for `open`: watch the vendor's overlay come and go.
   * Handles the overlay's OWN close control (which we never see a callback for)
   * as well as the initial appearance after `toggle()`.
   *
   * Mirrors the reference CMS's `overlayObserver`
   * (`theatre-detail-page.js` / `seatplan.js`), which watches the same class
   * transition to know when to (re-)inject its ticket counter.
   */
  useEffect(() => {
    if (!awaitingOverlay && !open) return;

    // If the overlay never materialises, stop waiting — the bottom bar stays
    // visible rather than the page sitting in a half-open state with nothing on
    // screen. Armed BEFORE the `root` guard below: when the widget never
    // mounted at all (no `eventUrlId` — the Seat Selection Page's own
    // reverse-resolve can come back empty), there is nothing to observe, and
    // leaving `awaitingOverlay` latched would pop the overlay open later if the
    // widget arrived on its own.
    const giveUp = awaitingOverlay
      ? setTimeout(() => setAwaitingOverlay(false), OVERLAY_WAIT_MS)
      : null;

    const root = document.getElementById('ltd-calendar-widget');
    if (!root) {
      return () => {
        if (giveUp) clearTimeout(giveUp);
      };
    }

    const isVendorOpen = () => !!root.querySelector(VENDOR_OVERLAY_SELECTOR);

    const sync = () => {
      if (isVendorOpen()) {
        setAwaitingOverlay(false);
        setOpen(true);
      } else if (openRef.current) {
        setOpen(false);
      }
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, childList: true, subtree: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      if (giveUp) clearTimeout(giveUp);
    };
  }, [awaitingOverlay, open]);

  // Body scroll lock — the vendor overlay is `position: fixed; inset: 0`, so
  // without this the page underneath scrolls behind it on touch.
  useEffect(() => {
    if (!open) return;
    document.body.classList.add(BODY_LOCK_CLASS);
    return () => {
      document.body.classList.remove(BODY_LOCK_CLASS);
    };
  }, [open]);

  // Escape closes, same as any modal.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') closeSheet();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, closeSheet]);

  // Rotating to landscape / resizing past the breakpoint must not leave the
  // overlay stuck over the desktop layout.
  useEffect(() => {
    if (!open) return;
    function onResize() {
      if (window.innerWidth > MOBILE_CALENDAR_BREAKPOINT) closeSheet();
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, closeSheet]);

  return {
    open,
    openSheet,
    closeSheet,
    sheetProps: { 'data-mobile-calendar-open': open ? 'true' : 'false' },
  };
}

